import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { tokenService } from '@/services/token-service';
import type { TokenCampaignResponse } from '@/types/campaign';
import { useSessionStorage } from './session-storage';

interface TokenCampaignState {
  campaignId?: string;
  campaign?: TokenCampaignResponse;
  isPending: boolean;
}

export function useTokenCampaign(campaignId?: string) {
  const navigate = useNavigate();
  const { storeCampaign, clearCampaign, getCampaign } = useSessionStorage();
  const [state, setState] = useState<TokenCampaignState>(() => {
    if (!campaignId) {
      return { isPending: false };
    }

    const cachedCampaign = getCampaign(campaignId);
    if (cachedCampaign) {
      return { campaignId, campaign: cachedCampaign, isPending: false };
    }

    return { campaignId, isPending: true };
  });

  useEffect(() => {
    if (!campaignId) {
      setState({ isPending: false });
      return;
    }

    const cachedCampaign = getCampaign(campaignId);
    if (cachedCampaign) {
      setState({ campaignId, campaign: cachedCampaign, isPending: false });
      return;
    }

    let isMounted = true;

    setState({ campaignId, isPending: true });

    void tokenService
      .getCampaign({ campaignId })
      .then((campaign) => {
        if (isMounted) {
          if (campaign) {
            storeCampaign(campaign);
          } else {
            clearCampaign();
          }

          setState({ campaignId, campaign, isPending: false });
        }
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        clearCampaign();
        navigate('/500');
      });

    return () => {
      isMounted = false;
    };
  }, [campaignId, navigate]);

  const isCurrentCampaign = state.campaignId === campaignId;

  return {
    campaign: isCurrentCampaign ? state.campaign : undefined,
    isPending: Boolean(campaignId) && (!isCurrentCampaign || state.isPending),
  };
}
